import React, { ReactElement } from 'react';
import { lazyInject } from '../../ioc';
import { SubscriptionStore } from '../../stores/subscription.store';
import styles from './preferences.less';
import {
    Card,
    Checkbox,
    Form,
    Stack,
    BodyText,
    Button,
    Banner,
    Headline,
} from '@servicetitan/design-system';
import { FieldState } from 'formstate';
import { observer } from 'mobx-react';

export interface PreferencesProps {
    header: ReactElement;
    showUnsubscribeButton?: boolean;
    emailId: string;
    version?: string;
}

@observer
export class Preferences extends React.Component<PreferencesProps> {
    @lazyInject(SubscriptionStore) private marketingStore!: SubscriptionStore;

    handleUpdateSubscription = () => {
        this.marketingStore.updatePreferences(this.emailId);
    };

    handleUnsubscribe = () => {
        this.marketingStore.unsubscribe(this.emailId);
    };

    private get emailId(): string {
        return this.props.emailId;
    }

    private get isNewVersion(): boolean {
        return this.props.version === '1';
    }

    private getCheckbox(label: string, name: string, state: FieldState<boolean>) {
        return (
            <Stack alignItems="flex-start" className="m-b-3">
                <Checkbox
                    size="large"
                    id={name}
                    onChange={(_, checked) => state.onChange(checked)}
                    checked={state.value}
                    className="m-r-0"
                />
                <BodyText size="small" el="label" for={name}>
                    {label}
                </BodyText>
            </Stack>
        );
    }

    render() {
        const options = this.marketingStore.optionsForm.$;
        return (
            <div className={styles.container}>
                <Card raised className={styles.card}>
                    <Card.Section className="p-x-5 p-y-4">
                        {this.props.header}
                        <hr />
                        <BodyText size="small" className="m-t-2 m-b-3">
                            Would you like to receive only certain types of messages from us? Choose
                            the categories that matter to you below and update your preferences.
                        </BodyText>
                        {this.marketingStore.optionsForm.hasError && (
                            <Banner
                                status="critical"
                                title={this.marketingStore.optionsForm.error ?? ''}
                                className="m-b-2"
                            />
                        )}
                        <BodyText size="small" bold>
                            Categories
                        </BodyText>
                        <Form className="m-t-2">
                            <Form.Group grouped>
                                {this.getCheckbox(
                                    'Estimate follow-ups',
                                    'UnsoldEstimates',
                                    options.unsoldEstimates
                                )}
                                {this.getCheckbox(
                                    'Equipment service check-ins',
                                    'AgingEquipment',
                                    options.agingEquipment
                                )}
                                {this.getCheckbox(
                                    'Membership reminders',
                                    'ExpiringMemberships',
                                    options.expiringMemberships
                                )}
                                {this.getCheckbox(
                                    "“Haven't heard from you” reminders",
                                    'IdleAccount',
                                    options.idleAccount
                                )}

                                {this.isNewVersion && (
                                    <>
                                        {this.getCheckbox(
                                            'Recurring service reminders',
                                            'RecurringServices',
                                            options.recurringService
                                        )}
                                        {this.getCheckbox(
                                            'Newsletters',
                                            'Newsletter',
                                            options.newsletter
                                        )}
                                        {this.getCheckbox(
                                            'Thank yous',
                                            'ThankYou',
                                            options.thankYou
                                        )}
                                        {this.getCheckbox(
                                            'Welcomes',
                                            'CustomerWelcome',
                                            options.customerWelcome
                                        )}
                                        {this.getCheckbox(
                                            'Special announcements',
                                            'Other',
                                            options.other
                                        )}
                                    </>
                                )}

                                {!this.isNewVersion &&
                                    this.getCheckbox(
                                        'Newsletter, Promotions, and Special Announcements',
                                        'Other',
                                        options.other
                                    )}
                            </Form.Group>
                        </Form>

                        <Stack justifyContent="center" className="m-t-4">
                            <Button onClick={this.handleUpdateSubscription} full primary>
                                Update Preferences
                            </Button>
                        </Stack>

                        {this.props.showUnsubscribeButton ? (
                            <Stack justifyContent="center" className="m-t-1">
                                <Button full onClick={this.handleUnsubscribe}>
                                    Unsubscribe
                                </Button>
                            </Stack>
                        ) : undefined}
                        {this.props.showUnsubscribeButton && this.marketingStore.unsubscribed && (
                            <div className={styles.successContainer}>
                                <div className={styles.successImages}>
                                    <img src="/assets/images/mrk-subs-circle.svg" />
                                    <img src="/assets/images/mrk-subs-check.svg" />
                                </div>
                                <Headline bold className="m-t-3">
                                    Unsubscribe successful
                                </Headline>
                                <BodyText size="small" className="m-t-2">
                                    You'll no longer receive email marketing from us.
                                </BodyText>
                            </div>
                        )}
                        {this.marketingStore.submitted && (
                            <div className={styles.successContainer}>
                                <div className={styles.successImages}>
                                    <img src="/assets/images/mrk-subs-circle.svg" />
                                    <img src="/assets/images/mrk-subs-check.svg" />
                                </div>
                                <Headline className="m-t-3">
                                    Your subscription preferences have been updated.
                                </Headline>
                            </div>
                        )}
                    </Card.Section>
                </Card>
            </div>
        );
    }
}
